import {
  Component,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter, OnInit, ChangeDetectorRef, SimpleChanges, OnChanges,
} from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { ChannelConfig } from '@app/acquisition-chart/channel-config.interface';

@Component({
    selector: 'channel-list',
    templateUrl: './channel-list.component.html',
    styleUrls: ['./channel-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ChannelListComponent implements OnInit, OnChanges {
  @Input()
  public readonly title: string;

  @Input()
  public selectDefaultChannels: boolean;

  @Input()
  public readonly hideSelectDefault: boolean;

  @Input()
  public readonly untouchableChannels: string[] = [];

  @Input()
  public channels: ChannelConfig[] = [];

  @Input()
  public readonly defaultChannels: string[] = [];

  @Input()
  public readonly showSettings: boolean;

  @Input()
  public readonly virtualScrollWidth: number;

  @Input()
  public readonly disabled: boolean;

  @Output()
  public readonly selectDefault = new EventEmitter<void>();

  @Output()
  public readonly channelListChanged = new EventEmitter<void>();

  @Output()
  public readonly deSelectAll = new EventEmitter<void>();

  @Output()
  public readonly settingClicked = new EventEmitter<string>();

  @Output()
  public readonly openChanged = new EventEmitter<boolean>();

  @Output()
  public readonly channelSelected = new EventEmitter<string>();

  @Output()
  public readonly unitSystemChanged = new EventEmitter<string>();

  public searchText: string;

  public disableDeselectAll: boolean;

  constructor(
    private readonly cdr: ChangeDetectorRef
  ) { }

  public ngOnInit(): void {
    this.sortChannelsCheckList();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes.channels?.firstChange) {
      setTimeout(() => this.sortChannelsCheckList());
    }
  }

  public onOpenChanged(value: boolean): void {
    this.openChanged.emit(value);
  }

  public changeUnitSystem(newUnitSystem: string) {
    this.unitSystemChanged.emit(newUnitSystem);
  }

  public onSelectDefault(): void {
    if (this.selectDefaultChannels) {
      const defaultChannels = this.defaultChannels;

      this.channels.forEach((channel) => {
        channel.selected = defaultChannels.includes(channel.name);
      });

      this.setDisableDeselectAll();
      this.selectDefault.emit();
      this.sortChannelsCheckList();
    } else {
      this.deselectAll();
    }

    this.cdr.detectChanges();
  }

  public deselectAll(): void {
    this.channels.forEach((channel) => {
      if (!this.untouchableChannels.includes(channel.name)) {
        channel.selected = false;
      }
    });

    this.selectDefaultChannels = false;
    this.disableDeselectAll = true;

    this.deSelectAll.emit();
    this.sortChannelsCheckList();
  }

  public onChannelListChanged(event: MatCheckboxChange, name: string): void {
    if (event.checked) {
      this.channelSelected.emit(name);
    }
    this.channelListChanged.emit();
    setTimeout(() => this.sortChannelsCheckList());
    this.setDisableDeselectAll();
  }

  public onSettingClicked(name: string): void {
    this.settingClicked.emit(name);
  }

  public trackByFn(_: number, item: ChannelConfig): string {
    return item.name;
  }

  private setDisableDeselectAll(): void {
    this.disableDeselectAll = !this.channels.filter(channels => channels.selected).length;
  }

  private sortChannelsCheckList(): void {
    const itemsToKeepFirst = this.channels.filter(item => this.untouchableChannels.includes(item.name));
    const remainingItems = this.channels.filter(item => !this.untouchableChannels.includes(item.name));

    itemsToKeepFirst.sort(this.getDefaultSortModel.bind(this));
    remainingItems.sort(this.getDefaultSortModel.bind(this));

    this.channels = itemsToKeepFirst.concat(remainingItems);

    this.cdr.detectChanges();
  }

  private getDefaultSortModel(a: ChannelConfig, b: ChannelConfig): number {
    return ((a.selected === b.selected) ? 0 : a.selected ? -1 : 1) ||
      (a.name < b.name ? -1 : 1);
  }
}

